import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCare } from '../contexts/CareContext'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import EmptyState from '../components/shared/EmptyState'
import ProgressForm, { DOMAINS, TRAJECTORIES } from '../components/progress/ProgressForm'
import Sparkline from '../components/progress/Sparkline'
import BaselineStep from '../components/onboarding/BaselineStep'
import { BASELINE, BASELINE_ORDER, baselineLabel } from '../components/onboarding/baseline'

const TRAJ_LABEL = Object.fromEntries(TRAJECTORIES)
const TRAJ_CLS = { stable: 'badge', improvement: 'badge-green', gradual_decline: 'badge-amber', acute_change: 'badge-danger' }
const fmtMonth = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

function Delta({ cur, prev }) {
  if (cur == null || prev == null) return null
  const d = cur - prev
  if (d === 0) return <span className="muted" style={{ fontSize: 12 }}>±0</span>
  const up = d > 0
  return <span style={{ fontSize: 12, fontWeight: 700, color: up ? 'var(--green)' : 'var(--danger)' }}>{up ? '▲' : '▼'} {Math.abs(d)}</span>
}

export default function ProgressPage() {
  const { recipient, role, reload } = useCare()
  const navigate = useNavigate()
  const canManage = role === 'admin' || role === 'family_member'
  const [rows, setRows] = useState([]) // newest first
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [settingBaseline, setSettingBaseline] = useState(false)

  const load = useCallback(async () => {
    if (!recipient) return
    const { data } = await supabase
      .from('monthly_progress')
      .select('*')
      .eq('care_recipient_id', recipient.id)
      .order('month', { ascending: false })
      .limit(6)
    setRows(data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => { load() }, [load])

  if (!recipient || loading) return <LoadingSpinner label="Loading progress…" />

  const name = recipient.preferred_name || recipient.full_name
  const needsBaseline = recipient.baseline_mobility == null

  // Standalone baseline capture (from the "Set baseline" prompt below).
  if (settingBaseline) {
    return (
      <BaselineStep
        recipientId={recipient.id}
        name={name}
        stepLabel={null}
        introTitle="Set the baseline"
        introText={`A quick snapshot of how ${name} is doing right now — the reference point we'll track against.`}
        finishLabel="Save baseline"
        onComplete={async () => { await reload(); setSettingBaseline(false) }}
      />
    )
  }

  const current = rows[0]
  const previous = rows[1]
  const chrono = [...rows].reverse() // oldest -> newest for sparklines

  return (
    <div className="stack">
      <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, alignSelf: 'flex-start', padding: 0 }}>← Home</button>

      <div className="spread">
        <h1 className="title">Monthly progress</h1>
        {canManage && <button className="btn btn-sm" onClick={() => setForm({ existing: current && current.month === `${new Date().toISOString().slice(0, 7)}-01` ? current : null })}>+ Add</button>}
      </div>

      {/* No baseline yet — offer to set it (covers skipped onboarding / older recipients) */}
      {needsBaseline && canManage && (
        <button className="card card-tap card-tint" onClick={() => setSettingBaseline(true)} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 26 }}>🌱</span>
          <div className="grow">
            <div style={{ fontWeight: 700 }}>Set {name}'s baseline</div>
            <div className="muted" style={{ fontSize: 13 }}>A 90-second snapshot — the starting point we track against.</div>
          </div>
          <span className="btn btn-sm" style={{ pointerEvents: 'none' }}>Start</span>
        </button>
      )}

      {/* Functional baseline from onboarding — the starting reference point */}
      {BASELINE_ORDER.some((k) => recipient[BASELINE[k].key] != null) && (
        <div className="card card-tint stack" style={{ gap: 10 }}>
          <div className="row" style={{ gap: 8 }}>
            <span style={{ fontSize: 18 }}>🌱</span>
            <div>
              <div className="section-title">Starting baseline</div>
              <div className="muted" style={{ fontSize: 12 }}>Captured at onboarding · the reference point</div>
            </div>
          </div>
          {BASELINE_ORDER.map((k) => (
            <div key={k} className="spread">
              <span className="label">{BASELINE[k].label}</span>
              <span style={{ fontWeight: 600, textAlign: 'right' }}>{baselineLabel(k, recipient[BASELINE[k].key]) || '—'}</span>
            </div>
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState icon="📈" title="No progress entries yet" message={canManage ? 'Rate 10 functional domains once a month to track the trend.' : 'Monthly progress will appear here.'} action={canManage && <button className="btn" onClick={() => setForm({ existing: null })}>+ Add this month</button>} />
      ) : (
        <>
          <div className="card">
            <div className="spread">
              <div>
                <div className="section-title">Current month</div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>{fmtMonth(current.month)}</div>
              </div>
              {current.trajectory && <span className={`badge ${TRAJ_CLS[current.trajectory] || 'badge'}`}>{TRAJ_LABEL[current.trajectory]}</span>}
            </div>
            {current.notes && <p className="muted mt-8" style={{ fontSize: 14, margin: '8px 0 0' }}>{current.notes}</p>}
          </div>

          <div className="section-title">Domains {previous && <span className="muted" style={{ textTransform: 'none', fontWeight: 400 }}>· vs {fmtMonth(previous.month)}</span>}</div>
          <div className="stack" style={{ gap: 8 }}>
            {DOMAINS.map(([key, label]) => {
              const series = chrono.map((r) => r[key])
              return (
                <div key={key} className="card spread" style={{ alignItems: 'center' }}>
                  <div className="grow">
                    <div style={{ fontWeight: 600 }}>{label}</div>
                    <div className="row" style={{ gap: 8, marginTop: 2 }}>
                      <span style={{ fontWeight: 700, color: 'var(--teal)' }}>{current[key] ?? '—'}<span className="muted" style={{ fontWeight: 400 }}>/5</span></span>
                      <Delta cur={current[key]} prev={previous?.[key]} />
                    </div>
                  </div>
                  <Sparkline values={series} />
                </div>
              )
            })}
          </div>
        </>
      )}

      {form && (
        <ProgressForm recipientId={recipient.id} existing={form.existing} onCancel={() => setForm(null)} onDone={() => { setForm(null); load() }} />
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}
